import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Settings, 
  Database,
  CheckCircle,
  XCircle,
  RefreshCw,
  Info,
  Server,
  Plug
} from "lucide-react";
import { toast } from "sonner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { devWarn } from "../utils/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConnectionSettings = () => {
  const [settings, setSettings] = useState({
    host: "localhost",
    port: 9000,
    company_name: ""
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [tdsSections, setTdsSections] = useState([]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/settings/connection`);
      setSettings({
        host: response.data.host || "localhost",
        port: response.data.port || 9000,
        company_name: response.data.company_name || ""
      });
    } catch (err) {
      /* settings load is non-critical - defaults are used */
      devWarn("fetchSettings (non-critical):", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTdsSections = async () => {
    try {
      const response = await axios.get(`${API}/reference/tds-sections`);
      setTdsSections(response.data);
    } catch (err) {
      /* sections are supplementary reference data */
      devWarn("fetchTdsSections (non-critical):", err);
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchTdsSections();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings/connection`, settings);
      toast.success("Settings saved successfully");
    } catch (err) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await axios.post(`${API}/settings/test-connection`, settings);
      setTestResult(response.data);
      if (response.data.success) {
        toast.success("Connection test successful");
      } else {
        toast.warning(response.data.message);
      }
    } catch (err) {
      setTestResult({ success: false, message: "Connection test failed" });
      toast.error("Connection test failed");
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="connection-settings-page">
      <div className="page-header">
        <h1 className="page-title">Connection Settings</h1>
        <p className="page-subtitle">Configure Tally Prime ODBC connection settings</p>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* Connection Settings Card */}
        <Card data-testid="connection-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              Tally ODBC Configuration
            </CardTitle>
            <CardDescription>
              Configure the connection to Tally Prime ODBC server
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="host">Host / IP Address</Label>
              <Input
                id="host"
                value={settings.host}
                onChange={(e) => setSettings({ ...settings, host: e.target.value })}
                placeholder="localhost or 192.168.x.x"
                data-testid="host-input"
              />
              <p className="text-xs text-slate-500">
                IP address where Tally Prime is running
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="port">ODBC Port</Label>
              <Input
                id="port"
                type="number"
                value={settings.port}
                onChange={(e) => setSettings({ ...settings, port: parseInt(e.target.value) || 9000 })}
                placeholder="9000"
                data-testid="port-input"
              />
              <p className="text-xs text-slate-500">
                Default Tally ODBC port is 9000
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="company">Company Name (Optional)</Label>
              <Input
                id="company"
                value={settings.company_name}
                onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                placeholder="Enter company name"
                data-testid="company-input"
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
                {saving ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Settings className="w-4 h-4 mr-2" />
                )}
                Save Settings
              </Button>
              <Button 
                variant="outline" 
                onClick={handleTestConnection}
                disabled={testing}
                data-testid="test-connection-btn"
              >
                {testing ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Plug className="w-4 h-4 mr-2" />
                )}
                Test Connection
              </Button>
            </div>

            {/* Test Result */}
            {testResult && (
              <Alert className={testResult.success ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}>
                {testResult.success ? (
                  <CheckCircle className="h-4 w-4 text-emerald-600" />
                ) : (
                  <Info className="h-4 w-4 text-amber-600" />
                )}
                <AlertTitle className={testResult.success ? "text-emerald-800" : "text-amber-800"}>
                  {testResult.success ? "Connection Ready" : "ODBC Note"}
                </AlertTitle>
                <AlertDescription className={testResult.success ? "text-emerald-700" : "text-amber-700"}>
                  {testResult.message}
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        {/* Instructions Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Info className="w-5 h-5" />
              Setup Instructions
            </CardTitle>
            <CardDescription>
              How to enable ODBC in Tally Prime
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 text-sm">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2">Enable ODBC in Tally Prime:</h4>
                <ol className="list-decimal list-inside space-y-2 text-slate-700">
                  <li>Open Tally Prime</li>
                  <li>Press <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-xs">F12</kbd> → Configure</li>
                  <li>Go to <strong>Data Configuration</strong></li>
                  <li>Enable <strong>ODBC Server</strong></li>
                  <li>Set the port (default: 9000)</li>
                  <li>Save and restart Tally</li>
                </ol>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2">Alternative: Manual Upload</h4>
                <p className="text-blue-700">
                  If ODBC is not available, you can export data from Tally as CSV files and upload them manually through the Party Ledgers and TDS Transactions pages.
                </p>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-900 mb-2">Firewall Note</h4>
                <p className="text-amber-700">
                  Ensure the ODBC port (default: 9000) is allowed through your firewall if connecting from a different machine.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* TDS Sections Reference */}
      <Card className="mt-6" data-testid="tds-sections-reference">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5" />
            TDS Section Reference
          </CardTitle>
          <CardDescription>
            Auto-classification keywords and rates for TDS sections
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="tds-table">
              <thead>
                <tr>
                  <th>Section</th>
                  <th>Rate</th>
                  <th>Description</th>
                  <th>Keywords for Auto-Classification</th>
                </tr>
              </thead>
              <tbody>
                {tdsSections.map((section) => (
                  <tr key={section.section}>
                    <td>
                      <span className="section-badge">{section.section}</span>
                    </td>
                    <td className="font-mono">{section.rate}%</td>
                    <td>{section.description}</td>
                    <td className="text-sm text-slate-500">
                      {section.keywords?.slice(0, 5).join(", ")}
                      {section.keywords?.length > 5 && "..."}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConnectionSettings;
